import React, { useEffect, useState, useCallback, useRef } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Dropdown, Nav, Spinner, Badge, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from '../contexts/LoadingContext';
import jsPDF from "jspdf";

const API = process.env.REACT_APP_API;

const GRADES = [
  { key: 'grade_4',   label: '4',   bg: 'success' },
  { key: 'grade_3_5', label: '3.5', bg: 'success' },
  { key: 'grade_3',   label: '3',   bg: 'success' },
  { key: 'grade_2_5', label: '2.5', bg: 'primary' },
  { key: 'grade_2',   label: '2',   bg: 'primary' },
  { key: 'grade_1_5', label: '1.5', bg: 'warning' },
  { key: 'grade_1',   label: '1',   bg: 'warning' },
  { key: 'grade_0',   label: '0',   bg: 'danger'  },
];

export default function GradeDistributionReport() {
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) { thead.classList.remove('table-secondary'); thead.classList.add('table-primary'); }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) { thead.classList.remove('table-primary'); thead.classList.add('table-secondary'); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const authHeader = { headers: { authorization: `Bearer ${getToken()}` } };

  const [yearList, setYearList]         = useState([]);
  const [yearId, setYearId]             = useState(() => localStorage.getItem('yearId') || null);
  const [selectedYear, setSelectedYear] = useState('');
  const [classList, setClassList]       = useState([]);
  const [activeClass, setActiveClass]   = useState('1');
  const [roomList, setRoomList]         = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null); // { r_id, label }
  const [semester, setSemester]         = useState('1');
  const [roomInfo, setRoomInfo]         = useState(null);
  const [data, setData]                 = useState([]);
  const [loading2, setLoading2]         = useState(false);
  const [noData, setNoData]             = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`, authHeader)
    .then(res => {
      const list = res.data;
      setYearList(list);
      let id = localStorage.getItem('yearId');
      if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem('yearId', id); }
      setYearId(id);
      const found = list.find(y => String(y.year_id) === String(id));
      if (found) setSelectedYear(`ปีการศึกษา ${found.year_year}`);
    }).catch(() => {});

    axiosInstance.get(`${API}/getClassList`, authHeader)
    .then(res => {
      setClassList(res.data || []);
      if (res.data?.length > 0) setActiveClass(String(res.data[0].c_id));
    }).catch(() => {});
  }, []);

  const loadRooms = useCallback(() => {
    if (!yearId || !activeClass) return;
    axiosInstance.get(`${API}/getRoomListByClassId/${activeClass}/${yearId}`, authHeader)
    .then(res => {
      const rooms = res.data || [];
      setRoomList(rooms);
      if (rooms.length > 0) {
        setSelectedRoom({ r_id: rooms[0].r_id, label: `ม.${rooms[0].tb_class_c_id}/${rooms[0].r_no}` });
      } else {
        setSelectedRoom(null);
        setData([]);
      }
    })
    .catch(() => { setRoomList([]); setSelectedRoom(null); });
  }, [yearId, activeClass]);

  useEffect(() => { loadRooms(); }, [loadRooms]);

  const handleSelectYear = (id) => {
    const found = yearList.find(y => y.year_id === id);
    if (found) {
      setYearId(id);
      setSelectedYear(`ปีการศึกษา ${found.year_year}`);
      localStorage.setItem('yearId', id);
    }
  };

  const loadData = useCallback(() => {
    if (!selectedRoom) return;
    setLoading(true);
    setLoading2(true);
    setNoData(false);

    const p1 = axiosInstance.get(`${API}/getRoomInfoForGrade/${selectedRoom.r_id}`, authHeader)
      .then(res => setRoomInfo(res.data)).catch(() => setRoomInfo(null));

    const p2 = axiosInstance.get(`${API}/getGradeDistribution/${selectedRoom.r_id}/${semester}`, authHeader)
      .then(res => setData(res.data))
      .catch(err => { setData([]); if (err.response?.status === 404) setNoData(true); });

    Promise.all([p1, p2]).finally(() => { setLoading(false); setLoading2(false); });
  }, [selectedRoom, semester, setLoading]);

  useEffect(() => { loadData(); }, [loadData]);

  const roomLabel = selectedRoom?.label || '';

  const handleExportExcel = async () => {
    const XLSX = await import('xlsx');
    const header = ['รหัสวิชา', 'ชื่อวิชา', 'ครูผู้สอน', ...GRADES.map(g => `เกรด ${g.label}`), 'รวม'];
    const rows = data.map(r => [
      r.sub_subid,
      r.sub_subname + (r.sub_type === 'extra' ? ' (เพิ่มเติม)' : ''),
      r.teacher_name,
      ...GRADES.map(g => Number(r[g.key] ?? 0)),
      Number(r.total_students),
    ]);
    const title = [`ผลสัมฤทธิ์รายวิชา ${roomLabel} ภาคเรียนที่ ${semester}${roomInfo ? ` ปีการศึกษา ${roomInfo.year_year}` : ''}`];
    const ws = XLSX.utils.aoa_to_sheet([title, [], header, ...rows]);
    ws['!cols'] = [{ wch: 12 }, { wch: 30 }, { wch: 20 }, ...GRADES.map(() => ({ wch: 10 })), { wch: 8 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ผลสัมฤทธิ์รายวิชา');
    XLSX.writeFile(wb, `ผลสัมฤทธิ์รายวิชา${roomLabel}_ภาค${semester}_ปี${roomInfo ? roomInfo.year_year : ''}.xlsx`);
  };

  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const win = window.open('', '_blank', 'width=1200,height=900');
    win.document.write(`
      <html><head><title>ผลสัมฤทธิ์รายวิชา</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #999; padding: 3px 6px; text-align: center; }
        th { background: #ddeeff; }
        .text-left { text-align: left !important; }
      </style>
      </head><body onload="window.print()">${content}</body></html>
    `);
    win.document.close();
  };

  const handleExportPDF = async () => {
    const element = printRef.current;
    if (!element) return;
    const html2canvas = (await import('html2canvas')).default;

    // Clone and replace rowSpan/colSpan thead with single-row for html2canvas
    const clone = element.cloneNode(true);
    clone.style.cssText = 'position:absolute;top:-9999px;left:0;background:white;width:' + element.offsetWidth + 'px;';
    document.body.appendChild(clone);
    const thead = clone.querySelector('thead');
    if (thead) {
      thead.innerHTML = `<tr style="background:#ddeeff;text-align:center">
        <th style="width:90px;border:1px solid #ccc;padding:4px">รหัสวิชา</th>
        <th style="min-width:160px;text-align:left;border:1px solid #ccc;padding:4px">ชื่อวิชา</th>
        <th style="min-width:120px;border:1px solid #ccc;padding:4px">ครูผู้สอน</th>
        ${GRADES.map(g => `<th style="width:50px;border:1px solid #ccc;padding:4px">${g.label}</th>`).join('')}
        <th style="width:60px;border:1px solid #ccc;padding:4px">รวม</th>
      </tr>`;
    }

    const canvas = await html2canvas(clone, { scale: 2, useCORS: true });
    document.body.removeChild(clone);
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgW = pageWidth - 10;
    const imgH = (canvas.height * imgW) / canvas.width;
    let y = 5;
    let remaining = imgH;
    while (remaining > 0) {
      pdf.addImage(imgData, 'PNG', 5, y, imgW, imgH);
      remaining -= (pageHeight - 10);
      if (remaining > 0) { pdf.addPage(); y = 5 - (imgH - remaining); }
    }
    pdf.save(`ผลสัมฤทธิ์รายวิชา${roomLabel}_ภาค${semester}_ปี${roomInfo ? roomInfo.year_year : ''}.pdf`);
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">วัดผลและประเมินผล</Link></li>
              <li className="breadcrumb-item active">รายงานผลสัมฤทธิ์รายวิชา</li>
            </ol>
            <h4 className="main-title mb-0">รายงานผลสัมฤทธิ์รายวิชา</h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-year">
                {selectedYear || 'เลือกปีการศึกษา'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map(y => (
                  <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                    ปีการศึกษา {y.year_year}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <Row className="g-3">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="flex-column align-items-start gap-2">
                <Nav variant="tabs" activeKey={activeClass} onSelect={k => setActiveClass(k)} className="w-100">
                  {classList.map(c => (
                    <Nav.Item key={c.c_id}>
                      <Nav.Link eventKey={String(c.c_id)}>{c.c_class}</Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <div className="d-flex align-items-center gap-3 pt-1 w-100">
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted fs-sm">ห้อง:</span>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" size="sm" id="dropdown-room">
                        {selectedRoom ? selectedRoom.label : 'เลือกห้อง'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {roomList.map(r => (
                          <Dropdown.Item
                            key={r.r_id}
                            onClick={() => setSelectedRoom({ r_id: r.r_id, label: `ม.${r.tb_class_c_id}/${r.r_no}` })}
                          >
                            ม.{r.tb_class_c_id}/{r.r_no}
                            {r.t_name && <span className="text-muted ms-1 fs-sm">({r.t_name})</span>}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  <Nav variant="pills" activeKey={semester} onSelect={k => setSemester(k)}>
                    <Nav.Item><Nav.Link eventKey="1" className="py-1 px-3">ภาคเรียนที่ 1</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="2" className="py-1 px-3">ภาคเรียนที่ 2</Nav.Link></Nav.Item>
                  </Nav>

                  {roomInfo && (
                    <span className="badge bg-primary fs-sm fw-normal">ปีการศึกษา {roomInfo.year_year}</span>
                  )}
                  <div className="d-flex gap-1 ms-auto">
                    <Button variant="outline-primary" size="sm" onClick={handlePrint} disabled={data.length === 0}>
                      <i className="ri-printer-line me-1"></i>พิมพ์
                    </Button>
                    <Button variant="outline-success" size="sm" onClick={handleExportExcel} disabled={data.length === 0}>
                      <i className="ri-file-excel-line me-1"></i>ส่งออก Excel
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={handleExportPDF} disabled={data.length === 0}>
                      <i className="ri-file-pdf-line me-1"></i>ส่งออก PDF
                    </Button>
                  </div>
                </div>
              </Card.Header>

              <Card.Body className="p-2" style={{ overflowX: 'auto' }}>
                {loading2 ? (
                  <div className="text-center py-5"><Spinner animation="border" /></div>
                ) : noData || data.length === 0 ? (
                  <div className="text-center text-muted py-5">ไม่พบข้อมูลผลการเรียนในภาคเรียนนี้</div>
                ) : (
                  <div ref={printRef}>
                  <div className="text-center mb-2 fw-bold">
                    ผลสัมฤทธิ์รายวิชา {roomLabel} ภาคเรียนที่ {semester}
                    {roomInfo && <span> ปีการศึกษา {roomInfo.year_year}</span>}
                  </div>
                  <Table bordered hover size="sm" className="mb-0">
                    <thead>
                      <tr style={{ background: '#ddeeff' }} className="text-center">
                        <th rowSpan={2} style={{ verticalAlign: 'middle', width: 90 }}>รหัสวิชา</th>
                        <th rowSpan={2} style={{ verticalAlign: 'middle', minWidth: 160, textAlign: 'left' }}>ชื่อวิชา</th>
                        <th rowSpan={2} style={{ verticalAlign: 'middle', minWidth: 120 }}>ครูผู้สอน</th>
                        <th colSpan={8}>จำนวนนักเรียนที่ได้ผลการเรียน</th>
                        <th rowSpan={2} style={{ verticalAlign: 'middle', width: 60 }}>รวม</th>
                      </tr>
                      <tr style={{ background: '#ddeeff' }} className="text-center">
                        {GRADES.map(g => (
                          <th key={g.key} style={{ width: 50 }}>
                            <Badge bg={g.bg} style={{ fontSize: '0.75em' }}>{g.label}</Badge>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row, idx) => (
                        <tr key={idx}>
                          <td className="text-center">{row.sub_subid}</td>
                          <td>
                            {row.sub_subname}
                            {row.sub_type === 'extra' && <span className="text-muted ms-1" style={{ fontSize: '0.8em' }}>(เพิ่มเติม)</span>}
                          </td>
                          <td className="text-center">{row.teacher_name}</td>
                          {GRADES.map(g => (
                            <td key={g.key} className="text-center" style={{ fontWeight: Number(row[g.key]) > 0 ? 'bold' : 'normal', color: Number(row[g.key]) > 0 ? undefined : '#bbb' }}>
                              {row[g.key] ?? 0}
                            </td>
                          ))}
                          <td className="text-center fw-bold">{row.total_students}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
